import { Directive, EventEmitter, Host, Input, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { TreeDataSourceDirectiveBase, TreeViewComponent } from '@app/core/components/tree-view/tree-view.component';
import { RememberService } from '@app/core/services/remember.service';
import { ChildMailFoldersParams, GraphMailService, RootMailFoldersParams } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { MailFolder } from '@microsoft/microsoft-graph-types';
import { DropPosition, TreeItemAddRemoveArgs } from '@progress/kendo-angular-treeview';
import { BehaviorSubject, debounceTime, filter, from, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { EmailService } from './email.service';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { LoginChangedEvent } from '@microsoft/mgt-element';

@Directive({
    selector: '[ttEmailFolderTreeDataSource]',
})
export class EmailFolderTreeDataSourceDirective implements OnDestroy {
    @Input() userId?: string;
    @Input() top: number = 99999;
    @Input() skip: number = 0;

    public folders: GraphMailFolderNode[] = [];

    private mailbox = '';

    public initialized = new BehaviorSubject(false);

    public rebind$ = new Subject<void>();

    public destroy$ = new Subject<void>();

    constructor(
        @Host() private treeview: TreeViewComponent,
        private graphMailService: GraphMailService,
        private remember: RememberService,
        private base64: Base64UrlService,
        private email: EmailService,
        private graphAuth: GraphAuthService,
    ) {
        treeview.onTreeViewLoaded$.pipe(takeUntil(this.destroy$)).subscribe({ next: () => this.rebind$.next() });
        this.rebind$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(50),
                switchMap(() => this.init()),
            )
            .subscribe();

        this.graphAuth.stateChange$.subscribe({
            next: async (value: LoginChangedEvent) => {
                try {
                    await this.graphAuth.getAccount();
                } catch (error) {
                    console.error(error);
                    this.rebind$.next();
                }
            },
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userId'].firstChange || changes['userId'].currentValue !== changes['userId'].previousValue) {
            // this.init();

            this.rebind$.next();
        }
    }

    async init() {
        if (!this.treeview.treeView?.get(0)) {
            throw new Error('Use graphMailFoldersDataSource on <kendo-treeview>.');
        }

        if (this.treeview.ttRememberIdExpanded) {
            this.treeview.getLastExpandedState();
        }

        await this.getPersistedSortMap();

        this.treeview.treeView.get(0)!.nodes = [];
        this.folders = [];

        this.treeview.treeView.get(0)!.children = this.fetchChildren.bind(this) as any;
        this.treeview.treeView.get(0)!.hasChildren = this.hasChildren.bind(this) as any;
        this.treeview.treeView.get(0)!.textField = 'displayName';
        this.treeview.treeView.get(0)!.nodeDrop.subscribe(this.handleDrop.bind(this));
        this.folders = [];

        from(this.getRootMailFolders())
            .pipe(
                map((r: any) => (r?.value ?? r ?? []) as MailFolder[]),
                map((items) =>
                    items.map(
                        (f, i) =>
                            <GraphMailFolderNode>{
                                ...f,
                                // parentFolderId: null,
                                displayName: `${f.displayName} ${!!f.unreadItemCount && f.unreadItemCount > 0 ? `(${f.unreadItemCount})` : ''}`,
                                item_glyphicon: 'fas-folder',
                                item_glyphicon_color: 'orange',
                                orderby: i + 1,
                            },
                    ),
                ),
                tap((items) => (this.folders = this.applySort(items))),
            )
            .subscribe((items) => {
                if (!this.email.initialMailFoldersLoaded$.getValue()) this.email.initialMailFoldersLoaded$.next(true);
                this.mailbox = items[0].parentFolderId!;
                this.treeview.treeView!.get(0)!.nodes = items.slice();
                this.treeview.data$.next(items.slice());
            });
    }

    hasChildren(folder: GraphMailFolderNode): boolean {
        return (folder.childFolderCount ?? 0) > 0 || this.folders.some((f) => f.parentFolderId === folder.id);
    }

    fetchChildren = (folder: GraphMailFolderNode): Observable<GraphMailFolderNode[]> => {
        if (!folder) return of([]);

        const cached = this.folders.filter((f) => f.parentFolderId === folder.id);
        if (cached.length > 0) return of(cached);

        if (!this.hasChildren(folder)) return of([]);

        return from(
            this.graphMailService.getChildMailFolders({
                userId: this.userId,
                folderId: folder.id,
                top: this.top,
                skip: this.skip,
            } as ChildMailFoldersParams),
        ).pipe(
            map((r: any) => (r?.value ?? r ?? []) as GraphMailFolderNode[]),
            map((items) =>
                items.map((f, i) => ({
                    ...f,
                    parentFolderId: folder.id,
                    displayName: `${f.displayName} ${!!f.unreadItemCount && f.unreadItemCount > 0 ? `(${f.unreadItemCount})` : ''}`,
                    item_glyphicon: 'fas-folder',
                    item_glyphicon_color: 'orange',
                    orderby: i + 1,
                })),
            ),
            tap((items) => {
                this.folders.push(...this.applySort(items));
                this.treeview.data$.next(this.folders);
            }),
        );
    };

    private async getRootMailFolders(): Promise<MailFolder[]> {
        try {
            if (!this.userId) {
                return [];
            }
            return await this.graphMailService.getRootMailFolders({ userId: this.userId, top: this.top, skip: this.skip });
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    public sortMap: {
        [key: string]: {
            [key: string]: number;
        };
    } = {};

    private async getPersistedSortMap() {
        try {
            let value = (await this.remember.getLastStatus(this.getRememberKey()))[0].variablevalue;

            value = JSON.parse(this.base64.urlDecode(value));

            this.sortMap = value;
        } catch (error) {
            console.error(error);
        }
    }

    private applySort(items: GraphMailFolderNode[]) {
        try {
            return items.sort((a, b) => this.sortMap[a.parentFolderId!][a.id!] - this.sortMap[a.parentFolderId!][b.id!]);
        } catch (error) {
            console.error(error);
        }

        return items;
    }

    async handleDrop(args: TreeItemAddRemoveArgs) {
        const source = args.sourceItem.item.dataItem as GraphMailFolderNode;
        const dest = args.destinationItem.item.dataItem as GraphMailFolderNode;

        const oldParentId = source.parentFolderId;

        const newParentId = args.dropPosition === DropPosition.Over ? dest.id : dest.parentFolderId;

        this.removeItem(args);
        await this.addItem(args);

        this.renumberOrderBy(oldParentId!);
        this.renumberOrderBy(newParentId!);

        this.rebindTreeView();

        this.persistOrderMap();
    }

    private async addItem({ sourceItem, destinationItem, dropPosition, destinationTree }: TreeItemAddRemoveArgs) {
        console.log('addItem');
        console.dir(destinationItem);
        const source = sourceItem.item.dataItem as GraphMailFolderNode;
        const dest = destinationItem.item.dataItem as GraphMailFolderNode;
        let persistMove = false;
        if (dropPosition === DropPosition.Over) {
            persistMove = source.parentFolderId !== dest.id;
            source.parentFolderId = dest.id;

            if (!this.hasLoadedChildren(dest) && (dest.childFolderCount ?? 0) > 0) {
                console.log('hasLoadedchildren');
                destinationTree.childrenLoaded
                    .pipe(
                        filter(({ item }) => item.dataItem === dest),
                        take(1),
                    )
                    .subscribe(() => {
                        this.folders.push(source);
                        this.rebindTreeView();
                    });
            } else {
                console.log('push reg');
                this.folders.push(source);
            }

            dest.childFolderCount = (dest.childFolderCount ?? 0) + 1;

            if (sourceItem.parent) {
                sourceItem.parent.item.dataItem.childFolderCount = (sourceItem.parent.item.dataItem.childFolderCount ?? 1) - 1;
            }

            if (!destinationTree.isExpanded(dest, destinationItem.item.index)) {
                destinationTree.expandNode(dest, destinationItem.item.index);
            }
        } else {
            persistMove = source.parentFolderId !== dest.parentFolderId;

            source.parentFolderId = dest.parentFolderId;

            const shift = dropPosition === DropPosition.After ? 1 : 0;
            const targetIndex = this.folders.indexOf(dest) + shift;

            this.folders.splice(targetIndex, 0, source);
        }

        if (persistMove) {
            try {
                const response = await this.graphMailService.moveMailFolder({ userId: this.userId, destinationId: source.parentFolderId!, id: source.id! });
                console.dir(response);
            } catch (error) {
                console.error(error);
                this.init();
            }
        }
    }

    private removeItem({ sourceItem, sourceTree }: TreeItemAddRemoveArgs): void {
        const source = sourceItem.item.dataItem as GraphMailFolderNode;

        const idx = this.folders.indexOf(source);
        if (idx >= 0) this.folders.splice(idx, 1);

        if (sourceItem.parent) {
            const parent = sourceItem.parent.item.dataItem as GraphMailFolderNode;
            const stillHasChildren = this.hasLoadedChildren(parent);

            parent.childFolderCount = stillHasChildren ? Math.max(parent.childFolderCount ?? 0, 1) : 0;

            if (!stillHasChildren) {
                sourceTree.collapseNode(parent, sourceItem.parent.item.index);
            }
        }
    }

    private renumberOrderBy(parentId: string | null): void {
        const siblings = this.folders.filter((f) => (f.parentFolderId ?? null) === (parentId ?? null));

        siblings.forEach((f, i) => {
            f.orderby = i + 1;
        });
    }

    private persistOrderMap(): void {
        if (!this.treeview.ttRememberId) return;

        this.sortMap = { ...this.sortMap, ...this.getSortMap() };
        this.remember.remember(this.getRememberKey(), this.base64.urlEncode(JSON.stringify(this.sortMap)));
    }

    private getRememberKey() {
        return this.treeview.ttRememberId + '.sort';
    }

    /**
     * Retrieves the current sort map. Only applies to folders which have been loaded into the component.
     */
    private getSortMap() {
        const parentFolderIds = new Set(this.folders.map((item) => item.parentFolderId) as string[]);
        let map: { [key: string]: { [key: string]: number } } = {};

        parentFolderIds.forEach((id) => {
            map[id] = Object.fromEntries(this.folders.filter((item) => item.parentFolderId === id).map((f, i) => [f.id, i + 1]));
        });

        return map;
    }

    private rebindTreeView(): void {
        this.treeview.treeView!.get(0)!.nodes = [...this.folders.filter((item) => item.parentFolderId === this.mailbox)];
    }

    private hasLoadedChildren(folder: GraphMailFolderNode): boolean {
        return this.folders.some((f) => f.parentFolderId === folder.id);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
export interface GraphMailFolderNode extends MailFolder {
    orderby?: number;
}

// @Directive({
//     selector: '[ttEmailFolderTreeDataSource]',
// })
// export class EmailFolderTreeDataSourceDirective extends TreeDataSourceDirectiveBase<GraphMailFolderNode> {
//     @Input()
//     public userId?: string;
//     @Input()
//     public top: number = 99999;
//     @Input()
//     public skip: number = 0;

//     public folders: GraphMailFolderNode[] = [];

//     private mailbox = '';

//     public initialized = new BehaviorSubject(false);

//     constructor(
//         @Host() treeview: TreeViewComponent,
//         private graphMailService: GraphMailService,
//         private remember: RememberService,
//         private base64: Base64UrlService,
//     ) {
//         super(treeview);
//     }

//     override fetchRootNodes() {
//         return from(this.graphMailService.getRootMailFolders(this.rootParams())).pipe(
//             map((r: any) => (r?.value ?? r ?? []) as MailFolder[]),
//             map((items) =>
//                 items.map((f, i) => ({
//                     ...f,
//                     displayName: `${f.displayName} ${!!f.unreadItemCount && f.unreadItemCount > 0 ? `(${f.unreadItemCount})` : ''}`,
//                     item_glyphicon: 'fas-folder',
//                     item_glyphicon_color: 'orange',
//                     orderby: i + 1,
//                 })),
//             ),
//             tap((items) => (this.folders = this.applySort(items))),
//             tap((items) => (this.mailbox = items[0].parentFolderId!)),
//         );
//     }

//     override fetchChildren = (folder: GraphMailFolderNode): Observable<GraphMailFolderNode[]> => {
//         if (!folder) return of([]);

//         const cached = this.folders.filter((f) => f.parentFolderId === folder.id);
//         if (cached.length > 0) return of(cached);

//         if (!this.hasChildren(folder)) return of([]);

//         return from(
//             this.graphMailService.getChildMailFolders({
//                 userId: this.userId,
//                 folderId: folder.id,
//                 top: this.top,
//                 skip: this.skip,
//             } as ChildMailFoldersParams),
//         ).pipe(
//             map((r: any) => (r?.value ?? r ?? []) as GraphMailFolderNode[]),
//             map((items) =>
//                 items.map((f, i) => ({
//                     ...f,
//                     parentFolderId: folder.id,
//                     displayName: `${f.displayName} ${!!f.unreadItemCount && f.unreadItemCount > 0 ? `(${f.unreadItemCount})` : ''}`,
//                     item_glyphicon: 'fas-folder',
//                     item_glyphicon_color: 'orange',
//                     orderby: i + 1,
//                 })),
//             ),
//             tap((items) => {
//                 this.folders.push(...this.applySort(items));
//                 this.treeview.data$.next(this.folders);
//             }),
//         );
//     };

//     override hasChildren(folder: GraphMailFolderNode): boolean {
//         return (folder.childFolderCount ?? 0) > 0 || this.folders.some((f) => f.parentFolderId === folder.id);
//     }

//     async handleDrop(args: TreeItemAddRemoveArgs) {
//         const source = args.sourceItem.item.dataItem as GraphMailFolderNode;
//         const dest = args.destinationItem.item.dataItem as GraphMailFolderNode;

//         const oldParentId = source.parentFolderId;

//         const newParentId = args.dropPosition === DropPosition.Over ? dest.id : dest.parentFolderId;

//         this.removeItem(args);
//         await this.addItem(args);

//         this.renumberOrderBy(oldParentId!);
//         this.renumberOrderBy(newParentId!);

//         this.rebindTreeView();

//         this.persistOrderMap();
//     }

//     ngOnChanges(changes: SimpleChanges): void {
//         if (changes['userId'].firstChange || changes['userId'].currentValue !== changes['userId'].previousValue) {
//             this.init();
//         }
//     }

//     override async init(): Promise<void> {
//         await this.getPersistedSortMap();
//         await super.init();

//         if (!this.initialized.getValue()) {
//             this.initialized.next(true);
//         }
//     }

//     public sortMap: {
//         [key: string]: {
//             [key: string]: number;
//         };
//     } = {};

//     private async getPersistedSortMap() {
//         try {
//             let value = (await this.remember.getLastStatus(this.getRememberKey()))[0].variablevalue;

//             value = JSON.parse(this.base64.urlDecode(value));

//             this.sortMap = value;
//         } catch (error) {
//             console.error(error);
//         }
//     }

//     private applySort(items: GraphMailFolderNode[]) {
//         try {
//             return items.sort((a, b) => this.sortMap[a.parentFolderId!][a.id!] - this.sortMap[a.parentFolderId!][b.id!]);
//         } catch (error) {
//             console.error(error);
//         }

//         return items;
//     }

//     private async addItem({ sourceItem, destinationItem, dropPosition, destinationTree }: TreeItemAddRemoveArgs) {
//         const source = sourceItem.item.dataItem as GraphMailFolderNode;
//         const dest = destinationItem.item.dataItem as GraphMailFolderNode;
//         let persistMove = false;
//         if (dropPosition === DropPosition.Over) {
//             persistMove = source.parentFolderId !== dest.id;
//             source.parentFolderId = dest.id;

//             dest.childFolderCount = Math.max(dest.childFolderCount ?? 0, 1);

//             if (!this.hasLoadedChildren(dest) && (dest.childFolderCount ?? 0) > 0) {
//                 destinationTree.childrenLoaded
//                     .pipe(
//                         filter(({ item }) => item.dataItem === dest),
//                         take(1),
//                     )
//                     .subscribe(() => {
//                         this.folders.push(source);
//                         this.rebindTreeView();
//                     });
//             } else {
//                 this.folders.push(source);
//             }

//             if (!destinationTree.isExpanded(dest, destinationItem.item.index)) {
//                 destinationTree.expandNode(dest, destinationItem.item.index);
//             }
//         } else {
//             persistMove = source.parentFolderId !== dest.parentFolderId;

//             source.parentFolderId = dest.parentFolderId;

//             const shift = dropPosition === DropPosition.After ? 1 : 0;
//             const targetIndex = this.folders.indexOf(dest) + shift;

//             this.folders.splice(targetIndex, 0, source);
//         }

//         if (persistMove) {
//             try {
//                 const response = await this.graphMailService.moveMailFolder({ userId: this.userId, destinationId: source.parentFolderId!, id: source.id! });
//                 console.dir(response);
//             } catch (error) {
//                 console.error(error);
//                 this.init();
//             }
//         }
//     }

//     private removeItem({ sourceItem, sourceTree }: TreeItemAddRemoveArgs): void {
//         const source = sourceItem.item.dataItem as GraphMailFolderNode;

//         const idx = this.folders.indexOf(source);
//         if (idx >= 0) this.folders.splice(idx, 1);

//         if (sourceItem.parent) {
//             const parent = sourceItem.parent.item.dataItem as GraphMailFolderNode;
//             const stillHasChildren = this.hasLoadedChildren(parent);

//             parent.childFolderCount = stillHasChildren ? Math.max(parent.childFolderCount ?? 0, 1) : 0;

//             if (!stillHasChildren) {
//                 sourceTree.collapseNode(parent, sourceItem.parent.item.index);
//             }
//         }
//     }

//     private renumberOrderBy(parentId: string | null): void {
//         const siblings = this.folders.filter((f) => (f.parentFolderId ?? null) === (parentId ?? null));

//         siblings.forEach((f, i) => {
//             f.orderby = i + 1;
//         });
//     }

//     private persistOrderMap(): void {
//         if (!this.treeview.ttRememberId) return;

//         this.sortMap = { ...this.sortMap, ...this.getSortMap() };
//         this.remember.remember(this.getRememberKey(), this.base64.urlEncode(JSON.stringify(this.sortMap)));
//     }

//     private getRememberKey() {
//         return this.treeview.ttRememberId + '.sort';
//     }

//     /**
//      * Retrieves the current sort map. Only applies to folders which have been loaded into the component.
//      */
//     private getSortMap() {
//         const parentFolderIds = new Set(this.folders.map((item) => item.parentFolderId) as string[]);
//         let map: { [key: string]: { [key: string]: number } } = {};

//         parentFolderIds.forEach((id) => {
//             map[id] = Object.fromEntries(this.folders.filter((item) => item.parentFolderId === id).map((f, i) => [f.id, i + 1]));
//         });

//         return map;
//     }

//     private rebindTreeView(): void {
//         this.treeview.treeView!.get(0)!.nodes = [...this.folders.filter((item) => item.parentFolderId === this.mailbox)];
//     }

//     private hasLoadedChildren(folder: GraphMailFolderNode): boolean {
//         return this.folders.some((f) => f.parentFolderId === folder.id);
//     }

//     private rootParams(): RootMailFoldersParams {
//         return {
//             userId: this.userId,
//             top: this.top,
//             skip: this.skip,
//         };
//     }
// }
// export interface GraphMailFolderNode extends MailFolder {
//     orderby?: number;
// }
